import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { english, french, wolof } from './translations';
import { Language, UI_CONFIGURATION } from './ui.config';
const preferenceKey = 'ipm.language';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly document = inject(DOCUMENT);
  private readonly translate = inject(TranslateService);
  private readonly config = inject(UI_CONFIGURATION);
  readonly multiLanguageEnabled = this.config.multiLanguageEnabled;
  readonly language = signal<Language>(this.savedLanguage());
  readonly locale = computed(() => ({ fr: 'fr-FR', en: 'en-GB', wo: 'wo-SN' })[this.language()]);

  constructor() {
    this.translate.setTranslation('fr', french);
    this.translate.setTranslation('en', english);
    this.translate.setTranslation('wo', wolof);
    this.setLanguage(this.language(), false);
  }

  setLanguage(language: string, persist = true): void {
    if (language !== 'fr' && language !== 'en' && language !== 'wo') return;
    if (!this.config.multiLanguageEnabled && language !== this.config.defaultLanguage) return;
    this.language.set(language);
    this.translate.use(language).subscribe();
    this.document.documentElement.lang = language;
    this.document.title = this.text('IPM Workspace');
    if (persist) {
      try { this.document.defaultView?.localStorage.setItem(preferenceKey, language); } catch {}
    }
  }

  text(key: string, params?: Record<string, unknown>): string {
    this.language();
    return this.translate.instant(key, params);
  }

  money(value: number): string {
    return new Intl.NumberFormat(this.locale(), { style: 'currency', currency: 'EUR' }).format(value);
  }

  date(value: string): string {
    const date = new Date(`${value}T00:00:00Z`);
    return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat(this.locale(), {
      year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'UTC',
    }).format(date);
  }

  private savedLanguage(): Language {
    if (!this.config.multiLanguageEnabled) return this.config.defaultLanguage;
    try {
      const language = this.document.defaultView?.localStorage.getItem(preferenceKey);
      return language === 'fr' || language === 'en' || language === 'wo' ? language : this.config.defaultLanguage;
    }
    catch { return this.config.defaultLanguage; }
  }
}